import { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

import { IRootState } from 'Scripts/store';

import { useGetCartQuery, useUpdateCartRowMutation } from 'Services/cartApi';
import { handleCheckoutClickDatalayerPush } from 'Utils/dataLayerPushService';
import { BreakPointHooks } from 'Hooks/BreakPointHooks';
import useLockedBody from 'Hooks/useLockedBody';
import useTrapFocus from 'Hooks/useTrapFocus';

import CtaButton from 'Components/CtaButton';
import Divider from 'Components/Divider';
import Loader from 'Components/Loader';
import CartItemRow from 'Components/MiniCart/CartItemRow';
import CartItemSummary from 'Components/MiniCart/CartItemSummary';

import CloseIcon from 'Icons/closeThin.svg?react';

const { useGreater } = BreakPointHooks();

interface MiniCartProps {
    isOpen: boolean;
    setIsOpen: (state: boolean) => void;
    handleIsHover: () => void;
}

const MiniCart = ({ isOpen, setIsOpen, handleIsHover }: MiniCartProps) => {
    const { shoppingCart, orderDetails, translations, checkoutPage } =
        useSelector((state: IRootState) => state?.cart?.miniCartModel);

    const [, setLockedBody] = useLockedBody(false);
    const isGreater = useGreater('lg');
    const closeTrap = () => {
        setIsOpen(false);
    };
    const trapRef = useTrapFocus(isOpen, closeTrap);

    const [
        updateCartRow,
        {
            error: errorCartRow,
            isLoading: isLoadingCartRow,
            isError: isErrorCartRow,
        },
    ] = useUpdateCartRowMutation();

    const {
        isLoading: cartIsLoading,
        isError: isErrorCart,
        error: errorCart,
    } = useGetCartQuery();

    const handleCartUpdate = (
        id: string,
        quantity: string,
        type: 'decrement' | 'increment' | 'delete',
        systemId: string
    ) => updateCartRow({ articleNumber: id, quantity, type, systemId });

    useEffect(() => {
        if (isOpen && !isGreater) {
            setLockedBody(true);
        } else {
            setLockedBody(false);
        }
    }, [isOpen, isGreater]);

    useEffect(() => {
        if (isErrorCartRow && 'data' in errorCartRow) {
            const errMessage: { title?: string } = errorCartRow.data;
            toast.error(errMessage.title);
        }
    }, [isErrorCartRow, errorCartRow]);
    useEffect(() => {
        if (isErrorCart && 'data' in errorCart) {
            const errMessage: { title?: string } = errorCart.data;
            toast.error(errMessage.title);
        }
    }, [isErrorCart, errorCart]);

    return (
        <section
            ref={trapRef}
            className="flex h-full min-h-[416px] w-full flex-col bg-white p-4.5 pb-2.5 shadow-lg min-[550px]:w-[530px] min-[550px]:max-w-[530px]"
            onMouseEnter={() => {
                handleIsHover();
            }}
        >
            {cartIsLoading && (
                <div className="flex h-full flex-1 flex-col justify-center">
                    <Loader />
                </div>
            )}
            {shoppingCart?.totalQuantity > 0 && (
                <div className="flex w-full flex-auto flex-wrap">
                    <div className="flex w-full flex-col">
                        <div className="flex items-center justify-between">
                            <h3 className="my flex flex-wrap font-montserrat text-xl">
                                {translations.miniCartTitle} (
                                {shoppingCart.totalQuantity}
                                {shoppingCart.totalQuantity > 1
                                    ? ` ${translations.cartTitleAdditionalInfoPlural}`
                                    : ` ${translations.cartTitleAdditionalInfoSingular}`}
                                )
                            </h3>
                            <button
                                className="cursor-pointer"
                                onClick={() => setIsOpen(false)}
                            >
                                <CloseIcon className="h-7 w-7" />
                                <span className="sr-only">
                                    {translations.minicartClose}
                                </span>
                            </button>
                        </div>
                        <Divider margin="my-4" />
                    </div>
                    <CartItemRow
                        isLoading={isLoadingCartRow}
                        updateCartRow={handleCartUpdate}
                        items={shoppingCart?.orderItems}
                        translations={translations}
                    />
                    {orderDetails && (
                        <CartItemSummary
                            isLoading={isLoadingCartRow}
                            items={orderDetails}
                            translations={translations}
                        />
                    )}
                    <div className="sticky bottom-0 flex w-full flex-wrap items-baseline justify-between bg-white pb-2 pt-2">
                        <CtaButton
                            as="a"
                            ctaType="primary"
                            className="checkout-button w-full text-center font-semibold"
                            href={checkoutPage.href}
                            onClick={() => handleCheckoutClickDatalayerPush()}
                        >
                            {checkoutPage.text}
                        </CtaButton>
                    </div>
                </div>
            )}
        </section>
    );
};

export default MiniCart;
